import { LitElement, css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/badge/badge.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/dialog/dialog.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import { WithUserProfileConsumer } from '../mixins/with-user-profile-consumer';
import { WithActiveShopConsumer } from '../mixins/with-active-shop-consumer';
import { ApiFactory } from '../api/easypos-api';
import { IStaffMember } from '../api/model/requests';
import { styles as sharedStyles } from '../styles/shared-styles';

const Base = WithActiveShopConsumer(WithUserProfileConsumer(LitElement));

// N.7.4 - the owner's staff list.
//
// Staff here are INTERNAL to the shop: a membership row with a synthetic staff-{ulid} and no
// login behind it. They never sign in. The device holds the shop's session, and a staff PIN
// says who is working so a sale can carry their name.
//
// That is why this page never speaks of accounts, passwords or access. Everything on it is
// record keeping, and the copy says so: "PIN set", not "can sign in".
@customElement('staff-page')
export class StaffPage extends Base {
  @state() private _staff: IStaffMember[] = [];
  @state() private _loading = true;
  @state() private _error: string | null = null;
  @state() private _busy = false;
  @state() private _showRetired = false;

  // The edit dialog. `_editing` null with the dialog open means "adding someone new", which is
  // the only difference between the two flows - the fields are identical.
  @state() private _dialogOpen = false;
  @state() private _editing: IStaffMember | null = null;
  @state() private _formName = '';
  @state() private _formJobTitle = '';
  @state() private _formPhone = '';
  @state() private _formEmail = '';
  @state() private _formNotes = '';
  @state() private _formPin = '';

  private _loadedShopName: string | null = null;

  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        padding: 16px;
        max-width: 900px;
        margin: 0 auto;
        box-sizing: border-box;
        width: 100%;
      }
      .head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        margin-bottom: 4px;
      }
      .lede {
        color: var(--app-text-muted);
        font-size: 0.9rem;
        margin: 0 0 16px;
        max-width: 60ch;
      }
      .row {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 12px 14px;
        border: 1px solid var(--app-border);
        border-radius: var(--sl-border-radius-medium);
        background: var(--app-surface);
        margin-bottom: 8px;
      }
      .row.retired {
        opacity: 0.6;
      }
      .who {
        flex: 1;
        min-width: 0;
      }
      .name {
        font-weight: 600;
      }
      .meta {
        font-size: 0.82rem;
        color: var(--app-text-muted);
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        margin-top: 2px;
      }
      .empty {
        padding: 32px 16px;
        text-align: center;
        color: var(--app-text-muted);
      }
      .form-grid {
        display: grid;
        gap: 12px;
      }
      .pin-note {
        font-size: 0.82rem;
        color: var(--app-text-muted);
        margin: -4px 0 0;
      }
    `,
  ];

  override updated() {
    const shop = this.activeShop?.shopName ?? null;
    if (shop && shop !== this._loadedShopName) {
      this._loadedShopName = shop;
      void this._load();
    }
  }

  private get _isOwner(): boolean {
    return (this.activeShop?.roles ?? []).includes('owner');
  }

  private async _load() {
    const shop = this.activeShop?.shopName;
    if (!shop) return;
    this._loading = true;
    this._error = null;
    const r = await ApiFactory.authenticated().listStaff(shop);
    this._loading = false;
    if (!r.success || !r.data) {
      this._error = r.error ?? 'Could not load staff.';
      return;
    }
    this._staff = r.data;
  }

  private _openAdd() {
    this._editing = null;
    this._formName = '';
    this._formJobTitle = '';
    this._formPhone = '';
    this._formEmail = '';
    this._formNotes = '';
    this._formPin = '';
    this._error = null;
    this._dialogOpen = true;
  }

  private _openEdit(member: IStaffMember) {
    this._editing = member;
    this._formName = member.displayName;
    this._formJobTitle = member.jobTitle ?? '';
    this._formPhone = member.phoneNumber ?? '';
    this._formEmail = member.emailAddress ?? '';
    this._formNotes = member.notes ?? '';
    // Never pre-filled. A PIN is write-only: it is stored hashed and cannot be read back, and
    // showing a placeholder that looked like one would be a lie.
    this._formPin = '';
    this._error = null;
    this._dialogOpen = true;
  }

  private async _save() {
    const shop = this.activeShop?.shopName;
    if (!shop || this._busy) return;
    const name = this._formName.trim();
    if (!name) {
      this._error = 'A name is required.';
      return;
    }
    const pin = this._formPin.trim();
    if (this._editing === null && !/^\d{4,6}$/.test(pin)) {
      // Required when adding, because a staff member without a PIN can be attributed nothing
      // and the record would do nothing at all.
      this._error =
        'A 4-6 digit PIN is required so this person can be recorded at the till.';
      return;
    }
    if (pin && !/^\d{4,6}$/.test(pin)) {
      this._error = 'A PIN must be 4-6 digits.';
      return;
    }

    this._busy = true;
    this._error = null;
    const api = ApiFactory.authenticated();
    const r = this._editing
      ? await api.updateStaff(shop, this._editing.loginId, {
          displayName: name,
          jobTitle: this._formJobTitle.trim(),
          phoneNumber: this._formPhone.trim(),
          emailAddress: this._formEmail.trim(),
          notes: this._formNotes.trim(),
          // Omitted entirely when blank, so editing a phone number leaves the PIN alone.
          ...(pin ? { pin } : {}),
        })
      : await api.addStaff(shop, {
          displayName: name,
          pin,
          jobTitle: this._formJobTitle.trim() || undefined,
          phoneNumber: this._formPhone.trim() || undefined,
          emailAddress: this._formEmail.trim() || undefined,
          notes: this._formNotes.trim() || undefined,
        });
    this._busy = false;

    if (!r.success) {
      this._error = r.error ?? 'Could not save.';
      return;
    }
    this._dialogOpen = false;
    await this._load();
  }

  private async _retire(member: IStaffMember) {
    const shop = this.activeShop?.shopName;
    if (!shop || this._busy) return;
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().retireStaff(
      shop,
      member.loginId
    );
    this._busy = false;
    if (!r.success) {
      this._error = r.error ?? 'Could not retire this person.';
      return;
    }
    await this._load();
  }

  private async _reinstate(member: IStaffMember) {
    const shop = this.activeShop?.shopName;
    if (!shop || this._busy) return;
    this._busy = true;
    this._error = null;
    const r = await ApiFactory.authenticated().updateStaff(
      shop,
      member.loginId,
      { active: true }
    );
    this._busy = false;
    if (!r.success) {
      this._error = r.error ?? 'Could not bring this person back.';
      return;
    }
    await this._load();
  }

  private _renderRow(member: IStaffMember) {
    const details = [
      member.jobTitle,
      member.phoneNumber,
      member.emailAddress,
    ].filter(Boolean);
    return html`
      <div class="row ${member.active ? '' : 'retired'}">
        <div class="who">
          <div class="name">${member.displayName}</div>
          <div class="meta">
            ${details.map((d) => html`<span>${d}</span>`)}
            ${
              // "PIN set" rather than "can sign in": staff never sign in, and the copy must not
              // imply they can.
              member.pinSet
                ? html`<sl-badge variant="neutral" pill>PIN set</sl-badge>`
                : html`<sl-badge variant="warning" pill>No PIN</sl-badge>`
            }
            ${member.active ? nothing : html`<sl-badge variant="danger" pill>Retired</sl-badge>`}
          </div>
        </div>
        ${
          this._isOwner
            ? html`
                <sl-button
                  size="small"
                  ?disabled=${this._busy}
                  @click=${() => this._openEdit(member)}
                >
                  Edit
                </sl-button>
                ${
                  member.active
                    ? html`<sl-button
                        size="small"
                        variant="text"
                        ?disabled=${this._busy}
                        @click=${() => void this._retire(member)}
                      >
                        Retire
                      </sl-button>`
                    : html`<sl-button
                        size="small"
                        variant="text"
                        ?disabled=${this._busy}
                        @click=${() => void this._reinstate(member)}
                      >
                        Bring back
                      </sl-button>`
                }
              `
            : nothing
        }
      </div>
    `;
  }

  render() {
    if (!this.activeShop) return html`<p>No active shop.</p>`;

    const visible = this._showRetired
      ? this._staff
      : this._staff.filter((m) => m.active);
    const retiredCount = this._staff.filter((m) => m.active === false).length;

    return html`
      <div class="head">
        <h1>Staff</h1>
        ${
          this._isOwner
            ? html`<sl-button
                variant="primary"
                ?disabled=${this._busy}
                @click=${this._openAdd}
              >
                <sl-icon slot="prefix" name="person-plus"></sl-icon>
                Add staff
              </sl-button>`
            : nothing
        }
      </div>

      <p class="lede">
        People who work in this shop. They do not sign in: the tablet stays
        signed in to the shop, and a staff member enters their PIN at the till
        so their name goes on the sales they take.
      </p>

      ${this._error ? html`<sl-alert variant="danger" open>${this._error}</sl-alert>` : nothing}
      ${
        this._loading
          ? html`<div class="empty">Loading…</div>`
          : visible.length === 0
            ? html`<div class="empty">
                ${
                  this._staff.length === 0
                    ? 'Nobody added yet. Add the people who work here so their sales carry their name.'
                    : 'Nobody currently working here.'
                }
              </div>`
            : visible.map((m) => this._renderRow(m))
      }
      ${
        retiredCount > 0
          ? html`<sl-switch
              ?checked=${this._showRetired}
              @sl-change=${(e: Event) =>
                (this._showRetired = (e.target as HTMLInputElement).checked)}
            >
              Show retired (${retiredCount})
            </sl-switch>`
          : nothing
      }

      <sl-dialog
        class="full-page-on-phone"
        label=${this._editing ? 'Edit staff member' : 'Add staff member'}
        ?open=${this._dialogOpen}
        @sl-request-close=${() => (this._dialogOpen = false)}
      >
        <div class="form-grid">
          <sl-input
            label="Name"
            required
            .value=${this._formName}
            @sl-input=${(e: Event) => (this._formName = (e.target as HTMLInputElement).value)}
          ></sl-input>
          <sl-input
            label="Job title"
            placeholder="Cashier"
            .value=${this._formJobTitle}
            @sl-input=${(e: Event) => (this._formJobTitle = (e.target as HTMLInputElement).value)}
          ></sl-input>
          <sl-input
            label="Phone"
            type="tel"
            .value=${this._formPhone}
            @sl-input=${(e: Event) => (this._formPhone = (e.target as HTMLInputElement).value)}
          ></sl-input>
          <sl-input
            label="Email"
            type="email"
            .value=${this._formEmail}
            @sl-input=${(e: Event) => (this._formEmail = (e.target as HTMLInputElement).value)}
          ></sl-input>
          <sl-input
            label=${this._editing ? 'New PIN (leave blank to keep the current one)' : 'PIN'}
            type="password"
            inputmode="numeric"
            maxlength="6"
            .value=${this._formPin}
            @sl-input=${(e: Event) => (this._formPin = (e.target as HTMLInputElement).value)}
          ></sl-input>
          <p class="pin-note">
            4 to 6 digits. This is how they identify themselves at the till, not
            a password - it does not sign anyone in.
          </p>
          <sl-textarea
            label="Notes"
            rows="2"
            .value=${this._formNotes}
            @sl-input=${(e: Event) => (this._formNotes = (e.target as HTMLTextAreaElement).value)}
          ></sl-textarea>
        </div>

        <sl-button slot="footer" @click=${() => (this._dialogOpen = false)}
          >Cancel</sl-button
        >
        <sl-button
          slot="footer"
          variant="primary"
          ?loading=${this._busy}
          ?disabled=${this._busy}
          @click=${() => void this._save()}
        >
          ${this._editing ? 'Save' : 'Add'}
        </sl-button>
      </sl-dialog>
    `;
  }
}
